import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { MeterBarStyleEnum } from '../../../../../../components/match/match-content/smart-widgets/move-meter-widget/meter/meter.enums';
import { MOVE_METER_TYPES } from '../../../../../../components/match/match-content/smart-widgets/consts';
import STRING_KEYS from '../../../../../../language/keys';
import { THEME } from '../../../../../../themes';
import MoveMeterChildWidgetsContainer
  from '../../../../../../components/match/match-content/smart-widgets/move-meter-widget/move-meter-child-widgets-container/move-meter-child-widgets-container.component';
import BaseWidget from '../base-widget/base-widget.component';
import { PerfectButtonHoverVariants } from '../../../../../../components/Common/Buttons/perfect-base-button/perfect-base-button.enums';
import BaseMenu from '../../../../../../components/Common/perfect-base-menu/perfect-base-menu.component';
import {
  Body, Icon, InfoButton, InfoContainer, InfoDescription, InfoIcon, InfoTitle, StyledMeter, TextContainer, Title,
} from './likelihood-to-move-widget.styled';

const MOVE_METER_TYPE_TO_SCORE = {
  [MOVE_METER_TYPES.SOMEWHAT_LIKELY_LOW]: 4,
  [MOVE_METER_TYPES.SOMEWHAT_LIKELY_HIGH]: 5,
  [MOVE_METER_TYPES.LIKELY_TO_MOVE_LOW]: 6,
  [MOVE_METER_TYPES.LIKELY_TO_MOVE_HIGH]: 7,
  [MOVE_METER_TYPES.VERY_LIKELY_LOW]: 8,
  [MOVE_METER_TYPES.VERY_LIKELY_HIGH]: 9,
};

const MOVE_METER_TYPE_TO_COLOR = {
  [MOVE_METER_TYPES.SOMEWHAT_LIKELY_LOW]: THEME.COLORS.WARNING,
  [MOVE_METER_TYPES.SOMEWHAT_LIKELY_HIGH]: THEME.COLORS.WARNING,
  [MOVE_METER_TYPES.LIKELY_TO_MOVE_LOW]: THEME.COLORS.SECONDARY,
  [MOVE_METER_TYPES.LIKELY_TO_MOVE_HIGH]: THEME.COLORS.SECONDARY,
  [MOVE_METER_TYPES.VERY_LIKELY_LOW]: THEME.COLORS.ACCENT,
  [MOVE_METER_TYPES.VERY_LIKELY_HIGH]: THEME.COLORS.ACCENT,
};

const MOVE_METER_LEVEL_TRANSLATION_KEY_MAP = {
  [MOVE_METER_TYPES.SOMEWHAT_LIKELY_LOW]: STRING_KEYS.MATCH_WIDGETS.LIKELIHOOD_TO_MOVE.LEVEL.LOW,
  [MOVE_METER_TYPES.SOMEWHAT_LIKELY_HIGH]: STRING_KEYS.MATCH_WIDGETS.LIKELIHOOD_TO_MOVE.LEVEL.LOW,
  [MOVE_METER_TYPES.LIKELY_TO_MOVE_LOW]: STRING_KEYS.MATCH_WIDGETS.LIKELIHOOD_TO_MOVE.LEVEL.MEDIUM,
  [MOVE_METER_TYPES.LIKELY_TO_MOVE_HIGH]: STRING_KEYS.MATCH_WIDGETS.LIKELIHOOD_TO_MOVE.LEVEL.MEDIUM,
  [MOVE_METER_TYPES.VERY_LIKELY_LOW]: STRING_KEYS.MATCH_WIDGETS.LIKELIHOOD_TO_MOVE.LEVEL.HIGH,
  [MOVE_METER_TYPES.VERY_LIKELY_HIGH]: STRING_KEYS.MATCH_WIDGETS.LIKELIHOOD_TO_MOVE.LEVEL.HIGH,
};

const TOTAL_BAR_SIZE = 10;

const LikelihoodToMoveWidget = ({
  data,
  childWidgets,
}: LikelihoodToMoveWidgetProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const level = MOVE_METER_TYPE_TO_SCORE[data.type] || 0;
  const color = MOVE_METER_TYPE_TO_COLOR[data.type];
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);

  const renderSubWidgetsContainer = () => {
    if (!childWidgets) {
      return null;
    }

    return (
      <MoveMeterChildWidgetsContainer childWidgets={ childWidgets } />
    );
  };

  return (
    <>
      <BaseWidget
        title={ translate(STRING_KEYS.MATCH_WIDGETS.LIKELIHOOD_TO_MOVE.TITLE) }
        Icon={ Icon }
        titleColor={ THEME.COLORS.PRIMARY_DARK }
        EndAdornment={ (
          <InfoButton
            ref={ infoButtonRef }
            StartIcon={ InfoIcon }
            hoverVariant={ PerfectButtonHoverVariants.Grow }
            onClick={ () => {
              if (anchorElement) {
                setAnchorElement(null);
              } else {
                setAnchorElement(infoButtonRef?.current);
              }
            } }
          />
        ) }
      >
        <div>
          <TextContainer>
            <Title color={ color } >{ translate(MOVE_METER_LEVEL_TRANSLATION_KEY_MAP[data.type]) }</Title>
          </TextContainer>
          <StyledMeter
            level={ level }
            totalBarSize={ TOTAL_BAR_SIZE }
            barStyle={ MeterBarStyleEnum.Horizontal }
            color={ color }
          />
          <Body>
            { renderSubWidgetsContainer() }
          </Body>
        </div>
      </BaseWidget>
      <BaseMenu
        isOpen={ !!anchorElement }
        onClose={ () => setAnchorElement(null) }
        anchorElement={ anchorElement || undefined }
        placement="bottom-end"
      >
        <InfoContainer>
          <InfoTitle>{translate(STRING_KEYS.LEARN_MORE_TITLE)}</InfoTitle>
          <InfoDescription>{translate(STRING_KEYS.LEARN_MORE_DESCRIPTIONS)}</InfoDescription>
        </InfoContainer>
      </BaseMenu>
    </>
  );
};

export default LikelihoodToMoveWidget;
